import React, { useState, useEffect, useMemo } from 'react';
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from 'reactstrap';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import _isArray from 'lodash/isArray';
import _reduce from 'lodash/reduce';
import _keys from 'lodash/keys';
import _get from 'lodash/get';
import classnames from 'classnames';

import PreventLink from '../PreventLink/PreventLink';
import NavProblemCount from './NavProblemCount';

import ProctorMonitorDropDown from './ProctorMonitorDropDown';
import { baseUrl, whiteLabelEnabled } from '../../constants/AppConstants';
import internetFallBack from '../../utils/internetFallBack';
import { showErrorToast } from '../../actions/toast';
import Timer from '../Timer/Timer';

import { getExpiryTime, retry } from '../../utils/utils';
import PhaseBadge from '../PhaseBadge';
import PlaceholderImage from '../PlaceholderImage/PlaceholderImage';
import { showAutoSubmitModalAction } from '../../actions/appState';

const ExitAssessment = React.lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ExitAssessmentChunk" */ '../SecureBrowser/ExitAssessment'
    )
  )
);

const ProbName = styled.div`
  display: flex,
  align-items: center;
  ${props =>
    !props.isAiInterview &&
    css`
      max-width: 250px;
    `}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props =>
    props.isAiInterview
      ? css`
          font-family: Lato;
          font-weight: 600;
          font-style: SemiBold;
          font-size: 24px !important;
          line-height: 32px;
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
        `
      : css`
          font-size: 17px !important;
          font-weight: 400;
        `}
  cursor: pointer;
  @media (max-width: 575.98px) {
    max-width: 126px;
  }
  @media (max-width: 767.98px) {
    margin: 0px;
    padding: 0px !important;
    margin-left: 17px;
    padding-top: 0.25rem !important;
  }
  @media (min-width: 768px) and (max-width: 991.98px) {
    max-width: 200px;
    margin-top: 4px;
  }
`;
const ProbText = styled.div`
  font-size: 17px;
  font-weight: 400;
`;
const UserNameEmail = styled.div`
  text-align: center;
  background: #f3f3f3;
  margin-top: -8px;
  font-size: 0.8rem;
  font-style: italic;
  color: #888;
  border-radius: 0.3rem 0.3rem 0px 0px;
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
`;
const ProbNavBar = styled(Navbar)`
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.3rem 0.3rem;
  max-height: 62px;
  min-height: var(--nav-bar-height);
  background-color: #101130;
  @media (max-width: 424.98px) {
    max-height: 108px;
  }
  @media (min-width: 425px) and (max-width: 767.98px) {
    padding: 0.3rem 0.3rem;
    align-items: flex-start;
    max-height: 90px;
  }
`;

const NavBrand = styled(NavbarBrand)`
  margin-top: 7px;
  @media (max-width: 767.98px) {
    margin-top: 0px;
    img {
      margin-top: 0px;
    }
  }
  @media (min-width: 768px) and (max-width: 991.98px) {
    margin-top: 5px;
  }
`;

const AiInterviewNavBrand = styled(NavBrand)`
  transform: translateX(-50%);
  left: 50%;
  position: absolute;
  top: 0;
  z-index: 1000;
`;

const BroadbandIcon = styled.div`
  height: 100%;
  margin: 0 8px;
  display: flex;
  align-items: center;
  color: white;
  width: 24px;
  .broadband-span:hover:after {
    left: unset !important;
    right: 30%;
  }
`;

function NavBar({ history }) {
  const auth = useSelector(state => state.auth);
  let email = '';
  if (auth && auth.user) {
    email = _get(auth, 'user.email');
  }
  const testSolutionSet = useSelector(state => state.testSolutionSet);

  const {
    imageProctoring,
    videoProctoring,
    proctoringEnabled,
  } = useMemo(() => {
    return {
      imageProctoring: _get(testSolutionSet, 'settings.proctor.snapshot'),
      videoProctoring: _get(testSolutionSet, 'settings.proctor.video.enabled'),
      proctoringEnabled: testSolutionSet?.settings?.proctor?.enabled,
    };
  }, [testSolutionSet]);

  const test = useSelector(state => state.test);

  const { isSecureBrowserEnable, isAiInterview } = test;
  const {
    serverTime,
    attemptedProblems,
    isSingleProblemView,
    isOffline,
    companyAvatarUrl,
    companyLogo,
    companyLogoStyle,
    showAutoSubmitModal,
  } = useSelector(
    state => ({
      serverTime: state.appStatus.serverTime,
      attemptedProblems: state.appStatus.attemptedProblems,
      isSingleProblemView: state.appStatus.isSingleProblemView,
      isOffline: state.appStatus.isOffline,
      companyAvatarUrl: state.test?.company?.avatar_url || '',
      companyLogo: state.whitelabelCompanyData.companyLogo || '',
      companyLogoStyle: state.whitelabelCompanyData.companyLogoStyle,
      showAutoSubmitModal: state.appStatus.showAutoSubmitModal,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const { attemptedCount } = useMemo(() => {
    let count = 0;
    if (attemptedProblems) count = _keys(attemptedProblems).length;
    return {
      attemptedCount: count,
    };
  }, [attemptedProblems]);

  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (_isArray(testSolutionSet.sections)) {
      const totalProblemCount = _reduce(
        testSolutionSet.sections,
        (acc, current) => acc + current.problem_ids.length,
        0
      );
      setTotal(totalProblemCount);
    }
  }, [testSolutionSet.sections]);

  const onRoute = () => {
    history.push({
      to: `/test/${test.slug}/`,
      state: {
        prevPath: isOffline ? history.location.pathname : `/test/${test.slug}/`,
      },
    });
  };

  const setErrorToast = message => {
    return dispatch(showErrorToast(message));
  };

  const isExtraTime =
    testSolutionSet.add_test_duration && testSolutionSet.add_test_duration > 0;
  const showTimer =
    !test.is_expired &&
    testSolutionSet.id &&
    (!isExtraTime || (isExtraTime && testSolutionSet.has_resumed_test));

  return (
    <ProbNavBar
      className={classnames('navbar-dark', 'fixed-top', {
        'd-none': isSingleProblemView,
        'align-items-center': isAiInterview,
      })}
      id="navbar"
      expand="lg"
      isAiInterview={isAiInterview}
    >
      <Nav className="flex-md-row" navbar>
        {!isAiInterview && (
          <NavBrand className="cursor-pointer">
            <PlaceholderImage
              src={companyLogo}
              alt="DoSelect"
              style={companyLogoStyle}
              id="do-test-logo1"
              placeholderHeight={whiteLabelEnabled ? '35px' : '30px'}
              placeholderWidth={whiteLabelEnabled ? '384px' : '148px'}
            />
          </NavBrand>
        )}
        <NavItem>
          <ProbName
            className="nav-link menu-link"
            onClick={() =>
              internetFallBack(
                isOffline,
                onRoute,
                'Action cannot be completed. Please check your internet connection.',
                setErrorToast
              )
            }
            isAiInterview={isAiInterview}
          >
            {test.name}
          </ProbName>
        </NavItem>
        <NavItem tag={PhaseBadge} small />
      </Nav>
      {isAiInterview && (
        <Nav
          className="flex-md-row justify-content-center align-items-center"
          navbar
        >
          <AiInterviewNavBrand className="cursor-pointer">
            <PlaceholderImage
              src={companyAvatarUrl || ''}
              alt="DoSelect"
              style={{
                height: '45px',
                width: '45px',
              }}
              id="do-test-logo"
              placeholderHeight="45px"
              placeholderWidth="45px"
            />
          </AiInterviewNavBrand>
        </Nav>
      )}

      {isSecureBrowserEnable && <ExitAssessment />}

      {!isAiInterview && (
        <Nav
          className="flex-md-row justify-content-end align-items-center"
          navbar
        >
          {showTimer && serverTime && !isSingleProblemView && (
            <Timer
              expiryTime={getExpiryTime(test, testSolutionSet)}
              onExpiry={() => {
                if (!showAutoSubmitModal) dispatch(showAutoSubmitModalAction());
              }}
            />
          )}
          {showTimer && (
            <NavProblemCount total={total} attempted={attemptedCount} />
          )}
        </Nav>
      )}

      <Nav
        className="flex-md-row justify-content-end align-items-center pt-2 pb-2"
        navbar
      >
        {isAiInterview && (
          <div className="d-flex flex-row justify-content-start align-items-center mr-5">
            {showTimer && serverTime && !isSingleProblemView && (
              <Timer
                isAiInterview={isAiInterview}
                expiryTime={getExpiryTime(test, testSolutionSet)}
                onExpiry={() => {
                  if (!showAutoSubmitModal)
                    dispatch(showAutoSubmitModalAction());
                }}
              />
            )}
          </div>
        )}
        <div className="d-flex flex-row justify-content-end align-items-center">
          {showTimer &&
            !isSingleProblemView &&
            (imageProctoring || videoProctoring) && (
              <NavItem>
                <ProctorMonitorDropDown
                  isSingleProblemView={isSingleProblemView}
                  id="topNavProctorMonitorPopover"
                />
              </NavItem>
            )}
          {showTimer &&
            !isSingleProblemView &&
            (proctoringEnabled || imageProctoring || videoProctoring) && (
              <NavItem>
                <BroadbandIcon id="broadbandIcon">
                  <span className="broadband-span good-broadband" />
                </BroadbandIcon>
              </NavItem>
            )}
          <NavItem>
            <PreventLink
              to={`/test/${test.slug}/help`}
              className="nav-link menu-link help-link"
            >
              <ProbText className="help-text">Help</ProbText>
              <ProbText>
                <i
                  className="fa fa-question-circle help-icon"
                  aria-hidden="true"
                />
              </ProbText>
            </PreventLink>
          </NavItem>
          {!whiteLabelEnabled ? (
            <UncontrolledDropdown
              className="hide-link"
              nav
              inNavbar
              id="do-test-manage-account"
            >
              <DropdownToggle nav>
                <ProbText>
                  <i className="fa fa-ellipsis-v" />
                </ProbText>
              </DropdownToggle>
              <DropdownMenu right>
                <UserNameEmail>
                  {email ? `Signed in as ${email}` : ''}
                </UserNameEmail>
                <DropdownItem
                  id="do-test-logout"
                  target="_self"
                  onClick={event => {
                    if (isOffline) {
                      event.preventDefault();
                      return internetFallBack(
                        isOffline,
                        null,
                        'Action cannot be completed. Please check your internet connection',
                        setErrorToast
                      );
                    }
                    return '';
                  }}
                  href={`${baseUrl}/accounts/logout`}
                >
                  <i className="fa fa-sign-out mr-2" />
                  Logout
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : (
            ''
          )}
        </div>
      </Nav>
    </ProbNavBar>
  );
}

export default withRouter(NavBar);
